import React, { useState, useEffect, useRef } from "react";
import { db } from "../../firebase";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { formatTimestamp as fmtTs } from "../../utils/dateFormat";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useLanguage } from "../../context/LanguageContext";
import {
  ClipboardList, MapPin, User,
  Calendar, XCircle, AlertTriangle,
  Target, Inbox, Settings, HardHat, Search, Download, ChevronDown,
} from "lucide-react";

// REPORT_TYPES and ALL_CUSTOM_TYPES labels are generated dynamically in the component using t()
const REPORT_TYPE_IDS = [
  { id: "visits",            Icon: ClipboardList, labelKey: "reports_type_visits",        descKey: "reports_type_visits_desc" },
  { id: "zone_coverage",     Icon: Target,        labelKey: "reports_type_zone_coverage",  descKey: "reports_type_zone_coverage_desc" },
  { id: "activity",          Icon: User,          labelKey: "reports_type_activity",        descKey: "reports_type_activity_desc" },
  { id: "alerts",            Icon: AlertTriangle, labelKey: "reports_type_alerts",           descKey: "reports_type_alerts_desc" },
  { id: "unauthorized_zone", Icon: XCircle,       labelKey: "reports_type_unauth_zone",     descKey: "reports_type_unauth_zone_desc" },
  { id: "zone",              Icon: MapPin,        labelKey: "reports_type_zone",             descKey: "reports_type_zone_desc" },
  { id: "daily",             Icon: Calendar,      labelKey: "reports_type_daily",            descKey: "reports_type_daily_desc" },
  { id: "client_creation",   Icon: HardHat,       labelKey: "reports_type_client_creation",  descKey: "reports_type_client_creation_desc" },
  { id: "custom",            Icon: Settings,      labelKey: "reports_type_custom",           descKey: "reports_type_custom_desc" },
];

const ALL_CUSTOM_TYPE_IDS = [
  { id: "checkin",             labelKey: "reports_custom_checkins" },
  { id: "zone_alert",          labelKey: "reports_custom_zone_alert" },
  { id: "speed_alert",         labelKey: "reports_custom_speed_alert" },
  { id: "long_stop_alert",     labelKey: "reports_custom_long_stop" },
  { id: "outside_hours_alert", labelKey: "reports_custom_outside_hours" },
  { id: "no_visits_alert",     labelKey: "reports_custom_no_visits" },
];

function ReportsPage({ companyId }) {
  const { t } = useLanguage();

  const REPORT_TYPES = REPORT_TYPE_IDS.map(r => ({ ...r, label: t(r.labelKey), desc: t(r.descKey) }));
  const ALL_CUSTOM_TYPES = ALL_CUSTOM_TYPE_IDS.map(r => ({ ...r, label: t(r.labelKey) }));

  const [selectedType, setSelectedType] = useState(null);
  const [vendors, setVendors]           = useState([]);
  const [sections, setSections]         = useState([]);
  const [filters, setFilters]           = useState({
    dateFrom: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    dateTo:   new Date().toISOString().slice(0, 10),
    timeFrom: "00:00",
    timeTo:   "23:59",
    vendorId: "all",
    zoneId:   "all",
    period:   "week",
  });
  const [results, setResults]         = useState([]);
  const [zoneCoverage, setZoneCoverage] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const [generated, setGenerated]     = useState(false);
  const [companyTimezone, setCompanyTimezone] = useState("Asia/Dubai");
  const [companyName,     setCompanyName]     = useState("");
  const [companyLogo,     setCompanyLogo]     = useState("");
  const [primaryColor,    setPrimaryColor]    = useState("#1a56a0");
  const [vendorSearch,    setVendorSearch]    = useState("");
  const [zoneSearch,      setZoneSearch]      = useState("");
  const [anomalyEnabled,         setAnomalyEnabled]         = useState(true);
  const [zoneActivity,           setZoneActivity]           = useState({});
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const [zoneDropdownOpen,   setZoneDropdownOpen]   = useState(false);
  const vendorDropdownRef = useRef(null);
  const zoneDropdownRef   = useRef(null);
  const [unauthorizedZoneActivity, setUnauthorizedZoneActivity] = useState({});
  const [customEventTypes,       setCustomEventTypes]       = useState(
    ["checkin","zone_alert","speed_alert","long_stop_alert","outside_hours_alert","no_visits_alert"]
  );
  const [customGroupBy,          setCustomGroupBy]          = useState("vendor"); // vendor | zone | day | client
  const [opQuickFilter,          setOpQuickFilter]          = useState("this_month");
  const [opOperatorSearch, setOpOperatorSearch] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadVendorsAndSections(); }, []);

  // Close searchable dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(e.target)) {
        setVendorDropdownOpen(false);
      }
      if (zoneDropdownRef.current && !zoneDropdownRef.current.contains(e.target)) {
        setZoneDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function loadVendorsAndSections() {
    try {
      const [usersSnap, sectionsSnap, configSnap, profileSnap, rootSnap] = await Promise.all([
        getDocs(collection(db, "companies", companyId, "users")),
        getDocs(collection(db, "companies", companyId, "sections")),
        getDoc(doc(db, "companies", companyId, "config", "settings")),
        getDoc(doc(db, "companies", companyId, "profile", "info")),
        getDoc(doc(db, "companies", companyId)),
      ]);
      if (configSnap.exists()) {
        setCompanyTimezone(configSnap.data().timezone || "Asia/Dubai");
      }
      if (profileSnap.exists()) {
        const pd = profileSnap.data();
        if (pd.primary_color) setPrimaryColor(pd.primary_color);
        if (pd.logo_url)      setCompanyLogo(pd.logo_url);
      }
      if (rootSnap.exists()) {
        const rd = rootSnap.data();
        if (rd.name)     setCompanyName(rd.name);
        if (rd.logo_url && !companyLogo) setCompanyLogo(rd.logo_url);
        setAnomalyEnabled(rd.anomaly_detection_enabled !== false);
      }
      // Vendor filter must show vendors only — not operators or admins
      setVendors(usersSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(u => u.role === "vendor")
      );
      setSections(sectionsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Failed to load vendors/sections:", err);
    }
  }

  function setPeriod(period) {
    const now   = new Date();
    const today = now.toISOString().slice(0, 10);
    let from    = today;
    let to      = today;

    if (period === "today") {
      from = today; to = today;
    } else if (period === "week") {
      const d = new Date(now); d.setDate(d.getDate() - 7);
      from = d.toISOString().slice(0, 10); to = today;
    } else if (period === "month") {
      const d = new Date(now); d.setDate(1);
      from = d.toISOString().slice(0, 10); to = today;
    } else if (period === "last_month") {
      const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      from = d.toISOString().slice(0, 10);
      to   = e.toISOString().slice(0, 10);
    }
    setFilters(p => ({ ...p, dateFrom: from, dateTo: to, period }));
  }

  // ─── BUILD ZONE COVERAGE ─────────────────────────────────────────────────
  async function buildZoneCoverage(visitEvents) {
    // Load all clients and sections in parallel
    const [clientsSnap, sectionsSnap] = await Promise.all([
      getDocs(collection(db, "companies", companyId, "clients")),
      getDocs(collection(db, "companies", companyId, "sections")),
    ]);

    const allClients  = clientsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    const allSections = sectionsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    // Build set of visited client IDs from checkin events in date range
    const visitedClientIds = new Set(visitEvents.map(e => e.clientId || e.client_id).filter(Boolean));

    // Also include clients whose last_status is "arrived"/"done" and last_visited_at
    // falls within the report date range — keeps this consistent with the Coverage Map display
    const from = new Date(filters.dateFrom + "T" + filters.timeFrom + ":00");
    const to   = new Date(filters.dateTo   + "T" + filters.timeTo   + ":59");
    allClients.forEach(client => {
      if (client.last_status === "arrived" || client.last_status === "done") {
        const lv = client.last_visited_at;
        if (lv) {
          const lvDate = lv.toDate ? lv.toDate() : new Date(lv);
          if (lvDate >= from && lvDate <= to) visitedClientIds.add(client.id);
        }
      }
    });

    // Initialize coverage buckets — respect zone filter
    const coverage = {};
    const filteredSections = filters.zoneId === "all"
      ? allSections
      : allSections.filter(s => s.id === filters.zoneId);

    filteredSections.forEach(sec => {
      coverage[sec.id] = {
        sectionName:    sec.name || sec.id,
        totalClients:   0,
        visitedClients: 0,
        visited:        [],
        notVisited:     [],
      };
    });

    // "No Zone" bucket only when showing all zones (not when a specific zone is filtered)
    if (filters.zoneId === "all") {
      coverage["__none__"] = {
        sectionName:    "No Zone",
        totalClients:   0,
        visitedClients: 0,
        visited:        [],
        notVisited:     [],
      };
    }

    // Assign each client to its section bucket
    allClients.forEach(client => {
      const secId = client.section || "__none__";
      // When a specific zone is selected, skip clients not in that zone
      if (filters.zoneId !== "all" && secId !== filters.zoneId) return;
      const bucket = coverage[secId] || coverage["__none__"];
      if (!bucket) return; // safety: no bucket means filtered out
      bucket.totalClients++;
      const fullName = `${client.first_name || ""} ${client.last_name || ""}`.trim() || client.id;
      if (visitedClientIds.has(client.id)) {
        bucket.visitedClients++;
        bucket.visited.push(fullName);
      } else {
        bucket.notVisited.push(fullName);
      }
    });

    // Return only sections that have at least one client, sorted by name
    return Object.values(coverage)
      .filter(s => s.totalClients > 0)
      .sort((a, b) => a.sectionName.localeCompare(b.sectionName));
  }

  // ─── GENERATE REPORT ─────────────────────────────────────────────────────
  async function generateReport() {
    if (!selectedType) return;
    setLoading(true);
    setError("");
    setResults([]);
    setZoneCoverage([]);
    setZoneActivity({});
    setUnauthorizedZoneActivity({});

    try {
      const from = new Date(filters.dateFrom + "T" + filters.timeFrom + ":00");
      const to   = new Date(filters.dateTo   + "T" + filters.timeTo   + ":59");

      // Real event types in Firestore — "visit" type is never written, only "checkin" exists
      const eventTypeMap = {
        visits:            ["checkin"],
        zone_coverage:     ["checkin"],
        activity:          ["checkin", "speed_alert", "zone_alert", "long_stop_alert", "outside_hours_alert", "no_visits_alert"],
        alerts:            ["zone_alert"],
        unauthorized_zone: ["zone_alert"],
        zone:              ["checkin"],
        daily:             ["checkin", "zone_alert", "speed_alert", "long_stop_alert", "outside_hours_alert", "no_visits_alert"],
        custom:            customEventTypes.length > 0 ? customEventTypes : ["checkin"],
      };

      // No orderBy in Firestore query — avoids composite index requirement. Sort in JS below.
      const q = query(
        collection(db, "companies", companyId, "events"),
        where("timestamp", ">=", from),
        where("timestamp", "<=", to)
      );

      const snap = await getDocs(q);
      let events = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Sort newest-first in JS
      events.sort((a, b) => {
        const ta = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp || 0);
        const tb = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp || 0);
        return tb - ta;
      });

      // Filter by event type
      const eventTypes = eventTypeMap[selectedType] || [];
      events = events.filter(e => eventTypes.includes(e.type));

      // Filter by vendor — match both Firestore doc ID and Auth UID (manual check-ins store Auth UID)
      if (filters.vendorId !== "all") {
        const vendorDoc = vendors.find(v => v.id === filters.vendorId);
        const vendorIds = new Set([filters.vendorId]);
        if (vendorDoc?.uid) vendorIds.add(vendorDoc.uid);
        events = events.filter(e => vendorIds.has(e.vendor_id || e.vendorId || ""));
      }

      // Enrich events with client section for reports that need zone-based filtering
      // Runs for: zone report, unauthorized_zone, visits (when zone filter active), alerts (when zone filter active)
      const needsEnrichment = (
        selectedType === "zone" ||
        selectedType === "unauthorized_zone" ||
        (selectedType === "visits"  && filters.zoneId !== "all") ||
        (selectedType === "alerts"  && filters.zoneId !== "all") ||
        (selectedType === "zone_coverage")
      );
      let clientSectionMap = {};
      if (needsEnrichment) {
        try {
          const clientsSnap = await getDocs(collection(db, "companies", companyId, "clients"));
          clientsSnap.docs.forEach(d => { clientSectionMap[d.id] = d.data().section || null; });
          events = events.map(e => ({
            ...e,
            section: e.section || clientSectionMap[e.client_id || e.clientId] || null,
          }));
        } catch (cErr) {
          console.error("[SECTION ENRICH] client section lookup failed:", cErr.message, cErr.code);
        }
      }

      // Filter by zone
      if (filters.zoneId !== "all") {
        if (selectedType === "zone_coverage") {
          // handled in buildZoneCoverage
        } else {
          events = events.filter(e => (e.section || e.sectionId) === filters.zoneId);
        }
      }

      setResults(events);

      // Zone coverage — build per-section visited/not-visited stats
      if (selectedType === "zone_coverage") {
        const coverage = await buildZoneCoverage(events);
        setZoneCoverage(coverage);
      }

      // Zone/Geofence — group checkin events by zone name for display
      if (selectedType === "zone") {
        const activity = {};
        events.forEach(e => {
          const secId   = e.section || null;
          const secName = (secId && sections.find(s => s.id === secId)?.name) || secId || "No Zone";
          if (!activity[secName]) activity[secName] = [];
          activity[secName].push(e);
        });
        setZoneActivity(activity);
      }

      // Unauthorized Zone — group zone_alert events by vendor for display
      if (selectedType === "unauthorized_zone") {
        const byVendor = {};
        events.forEach(e => {
          const vname = e.vendor_name || e.vendor_id || e.vendorId || "Unknown";
          if (!byVendor[vname]) byVendor[vname] = [];
          byVendor[vname].push(e);
        });
        setUnauthorizedZoneActivity(byVendor);
      }

      setGenerated(true);
    } catch (err) {
      setError("Failed to generate report: " + err.message + (err.code ? ` (${err.code})` : ""));
    }
    setLoading(false);
  }

  // ─── VISIT SUMMARY ───────────────────────────────────────────────────────
  function buildVisitSummary(events) {
    const summary = {};
    events.forEach(e => {
      const vid = e.vendorId || e.vendor_id || "unknown";
      const key = vid;
      if (!summary[key]) {
        summary[key] = {
          vendorName:  e.vendorName || e.vendor_name || e.vendorId || e.vendor_id || "Unknown",
          totalVisits: 0,
          clients:     new Set(),
          lastVisit:   null,
        };
      }
      summary[key].totalVisits++;
      const cname = e.clientName || e.client_name;
      if (cname) summary[key].clients.add(cname);
      const ts = e.timestamp?.toDate ? e.timestamp.toDate() : new Date(e.timestamp);
      if (!summary[key].lastVisit || ts > summary[key].lastVisit) {
        summary[key].lastVisit = ts;
      }
    });
    return Object.values(summary);
  }

  // ─── PDF EXPORT ──────────────────────────────────────────────────────────
  async function exportPDF() {
    const pdfdoc   = new jsPDF();
    const typeMeta = REPORT_TYPES.find(t => t.id === selectedType);
    const now      = new Date().toLocaleString();
    const vendorLabel = filters.vendorId === "all"
      ? "All Vendors"
      : (() => {
          const v = vendors.find(v => v.id === filters.vendorId);
          return v ? `${v.first_name} ${v.last_name}` : filters.vendorId;
        })();

    // Parse primary color hex → RGB (fallback to brand blue)
    function hexToRgb(hex) {
      const h = hex.replace("#", "");
      if (h.length !== 6) return [26, 86, 160];
      return [
        parseInt(h.substring(0, 2), 16),
        parseInt(h.substring(2, 4), 16),
        parseInt(h.substring(4, 6), 16),
      ];
    }
    function lightenRgb([r, g, b], amount = 0.88) {
      return [
        Math.round(r + (255 - r) * amount),
        Math.round(g + (255 - g) * amount),
        Math.round(b + (255 - b) * amount),
      ];
    }
    const brandRgb  = hexToRgb(primaryColor);
    const lightRgb  = lightenRgb(brandRgb);

    // ── Header bar (full-width colored strip) ──────────────────────────────
    pdfdoc.setFillColor(...brandRgb);
    pdfdoc.rect(0, 0, 210, 30, "F");

    // Accent strip at very top
    pdfdoc.setFillColor(...lightenRgb(brandRgb, 0.3));
    pdfdoc.rect(0, 0, 210, 3, "F");

    // App name "PINPOINT"
    pdfdoc.setTextColor(255, 255, 255);
    pdfdoc.setFontSize(18);
    pdfdoc.setFont("helvetica", "bold");
    pdfdoc.text("PINPOINT", 14, 16);

    // Report type label on the right side of header
    pdfdoc.setFontSize(10);
    pdfdoc.setFont("helvetica", "normal");
    pdfdoc.text(`${typeMeta?.label} Report`, 14, 24);

    // Company logo (top-right of header) — convert via canvas for jsPDF compatibility
    if (companyLogo) {
      try {
        const base64 = await new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            try {
              const canvas = document.createElement("canvas");
              canvas.width  = img.naturalWidth  || img.width;
              canvas.height = img.naturalHeight || img.height;
              canvas.getContext("2d").drawImage(img, 0, 0);
              resolve(canvas.toDataURL("image/png"));
            } catch (_) { resolve(null); }
          };
          img.onerror = () => resolve(null);
          img.src = companyLogo;
        });
        if (base64) pdfdoc.addImage(base64, "PNG", 175, 5, 22, 22);
      } catch (_) {}
    }

    // ── Company info block ─────────────────────────────────────────────────
    let y = 38;
    if (companyName) {
      pdfdoc.setTextColor(...brandRgb);
      pdfdoc.setFontSize(13);
      pdfdoc.setFont("helvetica", "bold");
      pdfdoc.text(companyName, 14, y);
      y += 7;
    }

    // Divider line
    pdfdoc.setDrawColor(...brandRgb);
    pdfdoc.setLineWidth(0.5);
    pdfdoc.line(14, y, 196, y);
    y += 5;

    // Meta info grid
    pdfdoc.setTextColor(60, 60, 60);
    pdfdoc.setFontSize(8.5);
    pdfdoc.setFont("helvetica", "bold");
    pdfdoc.text("Generated:", 14, y);
    pdfdoc.setFont("helvetica", "normal");
    pdfdoc.text(now, 40, y);

    pdfdoc.setFont("helvetica", "bold");
    pdfdoc.text("Period:", 110, y);
    pdfdoc.setFont("helvetica", "normal");
    pdfdoc.text(`${filters.dateFrom} ${filters.timeFrom}  →  ${filters.dateTo} ${filters.timeTo}`, 125, y);
    y += 6;

    pdfdoc.setFont("helvetica", "bold");
    pdfdoc.text("Vendor:", 14, y);
    pdfdoc.setFont("helvetica", "normal");
    pdfdoc.text(vendorLabel, 40, y);

    pdfdoc.setFont("helvetica", "bold");
    pdfdoc.text("Total Records:", 110, y);
    pdfdoc.setFont("helvetica", "normal");
    pdfdoc.text(String(results.length), 142, y);
    y += 8;

    // Second divider
    pdfdoc.setDrawColor(220, 220, 220);
    pdfdoc.setLineWidth(0.3);
    pdfdoc.line(14, y, 196, y);
    y += 4;

    // ── Table ──────────────────────────────────────────────────────────────
    const tableStyles = {
      headStyles:         { fillColor: brandRgb, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
      alternateRowStyles: { fillColor: lightRgb },
      styles:             { fontSize: 8.5, cellPadding: 3 },
      columnStyles:       {},
      margin:             { left: 14, right: 14 },
    };

    if (selectedType === "visits") {
      const summary = buildVisitSummary(results);
      autoTable(pdfdoc, {
        startY: y,
        head: [["Vendor", "Total Visits", "Unique Clients", "Last Visit"]],
        body: summary.map(s => [
          s.vendorName,
          s.totalVisits,
          s.clients.size,
          s.lastVisit ? s.lastVisit.toLocaleString() : "—",
        ]),
        ...tableStyles,
      });

    } else if (selectedType === "zone_coverage") {
      const coverage = zoneCoverage.length > 0
        ? zoneCoverage
        : await buildZoneCoverage(results);
      autoTable(pdfdoc, {
        startY: y,
        head: [["Zone", "Total Clients", "Visited", "Not Visited", "Coverage %"]],
        body: coverage.map(z => [
          z.sectionName,
          z.totalClients,
          z.visitedClients,
          z.totalClients - z.visitedClients,
          `${z.totalClients > 0 ? Math.round((z.visitedClients / z.totalClients) * 100) : 0}%`,
        ]),
        ...tableStyles,
      });

    } else {
      autoTable(pdfdoc, {
        startY: y,
        head: [["Type", "Vendor", "Client / Zone", "Timestamp", "Duration"]],
        body: results.map(e => [
          e.type,
          e.vendorName || e.vendorId || "—",
          e.clientName || e.sectionName || "—",
          formatTimestamp(e.timestamp),
          e.duration ? `${e.duration}s` : "—",
        ]),
        ...tableStyles,
      });
    }

    // ── Footer on every page ───────────────────────────────────────────────
    const pageCount = pdfdoc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdfdoc.setPage(i);
      // Footer bar
      pdfdoc.setFillColor(...brandRgb);
      pdfdoc.rect(0, 286, 210, 11, "F");
      pdfdoc.setTextColor(255, 255, 255);
      pdfdoc.setFontSize(7.5);
      pdfdoc.setFont("helvetica", "bold");
      pdfdoc.text("PINPOINT", 14, 293);
      if (companyName) {
        pdfdoc.setFont("helvetica", "normal");
        pdfdoc.text(`· ${companyName}`, 36, 293);
      }
      pdfdoc.text(`Page ${i} of ${pageCount}`, 196, 293, { align: "right" });
    }

    pdfdoc.save(`pinpoint-${selectedType}-report-${filters.dateFrom}.pdf`);
  }

  // ─── EXCEL EXPORT ────────────────────────────────────────────────────────
  async function exportExcel() {
    const XLSX = await import("xlsx");
    let rows;

    if (selectedType === "visits") {
      const summary = buildVisitSummary(results);
      rows = summary.map(s => ({
        Vendor:           s.vendorName,
        "Total Visits":   s.totalVisits,
        "Unique Clients": s.clients.size,
        "Last Visit":     s.lastVisit ? s.lastVisit.toLocaleString() : "—",
      }));
    } else if (selectedType === "zone_coverage") {
      const coverage = zoneCoverage.length > 0
        ? zoneCoverage
        : await buildZoneCoverage(results);
      rows = coverage.map(z => ({
        Zone:            z.sectionName,
        "Total Clients": z.totalClients,
        Visited:         z.visitedClients,
        "Not Visited":   z.totalClients - z.visitedClients,
        "Coverage %":    `${z.totalClients > 0 ? Math.round((z.visitedClients / z.totalClients) * 100) : 0}%`,
        "Visited Names":     z.visited.join(", "),
        "Not Visited Names": z.notVisited.join(", "),
      }));
    } else {
      rows = results.map(e => ({
        Type:          e.type,
        Vendor:        e.vendor_name || e.vendor_id || "—",
        "Client/Zone": e.client_name || e.section_name || "—",
        Timestamp:     formatTimestamp(e.timestamp),
        Duration:      e.duration ? `${e.duration}s` : "—",
      }));
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `pinpoint-${selectedType}-report-${filters.dateFrom}.xlsx`);
  }

  // ─── CSV EXPORT ──────────────────────────────────────────────────────────
  async function exportCSV() {
    let rows;

    if (selectedType === "visits") {
      const summary = buildVisitSummary(results);
      rows = summary.map(s => ({
        vendor:          s.vendorName,
        total_visits:    s.totalVisits,
        unique_clients:  s.clients.size,
        last_visit:      s.lastVisit ? s.lastVisit.toLocaleString() : "",
      }));
    } else if (selectedType === "zone_coverage") {
      const coverage = zoneCoverage.length > 0
        ? zoneCoverage
        : await buildZoneCoverage(results);
      rows = coverage.map(z => ({
        zone:               z.sectionName,
        total_clients:      z.totalClients,
        visited:            z.visitedClients,
        not_visited:        z.totalClients - z.visitedClients,
        coverage_pct:       `${z.totalClients > 0 ? Math.round((z.visitedClients / z.totalClients) * 100) : 0}%`,
        visited_names:      z.visited.join(" | "),
        not_visited_names:  z.notVisited.join(" | "),
      }));
    } else {
      rows = results.map(e => ({
        type:        e.type,
        vendor:      e.vendor_name || e.vendor_id || "",
        client_zone: e.client_name || e.section_name || "",
        timestamp:   formatTimestamp(e.timestamp),
        duration:    e.duration ? `${e.duration}s` : "",
      }));
    }

    if (!rows || rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const csv = [headers, ...rows.map(r => headers.map(h => `"${r[h]}"`))]
      .map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = `pinpoint-${selectedType}-report-${filters.dateFrom}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function formatTimestamp(ts) {
    return fmtTs(ts, companyTimezone);
  }

  // ─── CLIENT CREATION REPORT STATE ────────────────────────────────────────
  const [opClients,        setOpClients]        = useState([]);
  const [opOperators,      setOpOperators]      = useState([]);
  const [opLoading,        setOpLoading]        = useState(false);
  const [opGenerated,      setOpGenerated]      = useState(false);
  const [opAllTimeCounts,  setOpAllTimeCounts]  = useState({});
  const [opSearch,         setOpSearch]         = useState("");
  const [opOperatorFilter, setOpOperatorFilter] = useState("all");
  const [opDateFrom,  setOpDateFrom]  = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  );
  const [opDateTo, setOpDateTo] = useState(new Date().toISOString().slice(0, 10));

  // Pre-load operators as soon as client_creation report is selected
  useEffect(() => {
    if (selectedType !== "client_creation") return;
    if (opOperators.length > 0) return; // already loaded
    async function loadOperators() {
      try {
        const snap = await getDocs(
          query(collection(db, "companies", companyId, "users"), where("role", "==", "operator"))
        );
        setOpOperators(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("load operators error:", err.message);
      }
    }
    loadOperators();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);

  async function generateClientCreationReport() {
    setOpLoading(true);
    setOpGenerated(false);
    try {
      // Load all clients in company
      const clientsSnap = await getDocs(collection(db, "companies", companyId, "clients"));
      const allClients  = clientsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Load all operators
      const usersSnap   = await getDocs(
        query(collection(db, "companies", companyId, "users"), where("role", "==", "operator"))
      );
      const ops = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setOpOperators(ops);

      // Filter clients by date range and optional operator filter
      // When "all" is selected — include ALL clients in the period regardless of creator role
      const from = new Date(opDateFrom + "T00:00:00");
      const to   = new Date(opDateTo   + "T23:59:59");

      const filtered = allClients.filter(c => {
        if (!c.created_at) return false;
        if (opOperatorFilter !== "all" && c.created_by !== opOperatorFilter) return false;
        const d = c.created_at.toDate ? c.created_at.toDate() : new Date(c.created_at);
        return d >= from && d <= to;
      });

      // All-time totals per operator (no date filter)
      const allTimeCounts = {};
      ops.forEach(op => {
        allTimeCounts[op.id] = allClients.filter(c => c.created_by === op.id).length;
      });
      setOpAllTimeCounts(allTimeCounts);

      setOpClients(filtered);
      setOpGenerated(true);
    } catch (err) {
      console.error("client creation report error:", err.message);
    }
    setOpLoading(false);
  }

  function setOpPeriod(period) {
    const now   = new Date();
    const today = now.toISOString().slice(0, 10);
    if (period === "today") {
      setOpDateFrom(today); setOpDateTo(today);
    } else if (period === "yesterday") {
      const d = new Date(now); d.setDate(d.getDate() - 1);
      const yest = d.toISOString().slice(0, 10);
      setOpDateFrom(yest); setOpDateTo(yest);
    } else if (period === "this_week") {
      const d = new Date(now); d.setDate(d.getDate() - 7);
      setOpDateFrom(d.toISOString().slice(0, 10)); setOpDateTo(today);
    } else if (period === "this_month") {
      const d = new Date(now); d.setDate(1);
      setOpDateFrom(d.toISOString().slice(0, 10)); setOpDateTo(today);
    }
    setOpQuickFilter(period);
  }

  async function exportClientCreationExcel() {
    const XLSX = await import("xlsx");

    // Summary sheet — one row per operator
    const now      = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const weekAgo  = new Date(now); weekAgo.setDate(now.getDate() - 7);
    const monthAgo = new Date(now); monthAgo.setMonth(now.getMonth() - 1);

    const summaryRows = opOperators.map(op => {
      const mine = opClients.filter(c => c.created_by === op.id);
      const getD = ts => ts?.toDate ? ts.toDate() : new Date(ts);
      return {
        Operator:      `${op.first_name || ""} ${op.last_name || ""}`.trim(),
        Email:         op.email || "—",
        Today:         mine.filter(c => c.created_at && getD(c.created_at).toISOString().slice(0,10) === todayStr).length,
        "This Week":   mine.filter(c => c.created_at && getD(c.created_at) >= weekAgo).length,
        "This Month":  mine.filter(c => c.created_at && getD(c.created_at) >= monthAgo).length,
        Total:         mine.length,
      };
    });

    // Detail sheet — one row per client
    const detailRows = opClients.map(c => {
      const op = opOperators.find(o => o.id === c.created_by);
      return {
        "First Name":    c.first_name || "—",
        "Last Name":     c.last_name  || "—",
        Phone:           c.phone      || "—",
        Address:         c.address    || "—",
        "Created By":    op ? `${op.first_name || ""} ${op.last_name || ""}`.trim() : (c.created_by_name || "—"),
        "Date Added":    c.created_at ? formatTimestamp(c.created_at) : "—",
      };
    });

    const wb  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), "By Operator");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailRows),  "All Clients");
    XLSX.writeFile(wb, `pinpoint-client-creation-${opDateFrom}-to-${opDateTo}.xlsx`);
  }

  // ─────────────────────────────────────────────────────────────────────────

  const eventColors = {
    visit:               "bg-green-100 text-green-700",
    checkin:             "bg-green-100 text-green-700",
    zone_alert:          "bg-red-100 text-red-700",
    speed_alert:         "bg-yellow-100 text-yellow-700",
    long_stop_alert:     "bg-orange-100 text-orange-700",
    outside_hours_alert: "bg-purple-100 text-purple-700",
    no_visits_alert:     "bg-gray-100 text-gray-600",
    session_start:       "bg-blue-100 text-blue-700",
    session_end:         "bg-gray-100 text-gray-700",
  };

  const visitSummary = generated && selectedType === "visits" ? buildVisitSummary(results) : [];

  // zone_coverage, zone, unauthorized_zone, daily, custom have their own display — don't show generic empty state for them
  const showEmpty = generated && !["zone_coverage", "zone", "unauthorized_zone", "daily", "custom"].includes(selectedType) && results.length === 0;
  const showZoneEmpty = generated && selectedType === "zone_coverage" && zoneCoverage.length === 0;
  const showZoneActivityEmpty = generated && selectedType === "zone" && Object.keys(zoneActivity).length === 0;
  const showUnauthorizedZoneEmpty = generated && selectedType === "unauthorized_zone" && Object.keys(unauthorizedZoneActivity).length === 0;

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-800">{t("reports_title")}</h2>
        <p className="text-gray-500 text-sm mt-1">{t("reports_subtitle")}</p>
      </div>

      {/* Step 1 — Report Type */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("reports_step1")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {REPORT_TYPES.map(type => (
            <button key={type.id}
              onClick={() => {
                setSelectedType(type.id);
                setGenerated(false);
                setResults([]);
                setZoneCoverage([]);
                setZoneActivity({});
                setUnauthorizedZoneActivity({});
                setVendorSearch("");
                setZoneSearch("");
                // Reset client_creation state so stale results don't persist
                setOpGenerated(false);
                setOpClients([]);
                setOpAllTimeCounts({});
                setOpSearch("");
                setOpOperatorFilter("all");
                setOpOperatorSearch("");
              }}
              className={`p-4 rounded-xl border-2 text-left transition ${
                selectedType === type.id
                  ? "border-green-500 bg-green-50"
                  : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}>
              <type.Icon className="w-6 h-6 mb-2 text-gray-500" />
              <p className="text-sm font-medium text-gray-800">{type.label}</p>
              <p className="text-gray-400 text-xs mt-1">{type.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Step 2 — Filters (not shown for client_creation — it has its own date range) */}
      {selectedType && selectedType !== "client_creation" && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("reports_step2")}</h3>

          {/* Quick periods */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {[
              { key: "today",      label: t("reports_period_today") },
              { key: "week",       label: t("reports_period_week") },
              { key: "month",      label: t("reports_period_month") },
              { key: "last_month", label: t("reports_period_last_month") },
              { key: "custom",     label: t("reports_period_custom") },
            ].map(p => (
              <button key={p.key} onClick={() => setPeriod(p.key)}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition ${
                  filters.period === p.key
                    ? "bg-green-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}>
                {p.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("reports_from_date")}</label>
              <input type="date" value={filters.dateFrom}
                onChange={e => setFilters(p => ({ ...p, dateFrom: e.target.value, period: "custom" }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("reports_from_time")}</label>
              <input type="time" value={filters.timeFrom}
                onChange={e => setFilters(p => ({ ...p, timeFrom: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("reports_to_date")}</label>
              <input type="date" value={filters.dateTo}
                onChange={e => setFilters(p => ({ ...p, dateTo: e.target.value, period: "custom" }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("reports_to_time")}</label>
              <input type="time" value={filters.timeTo}
                onChange={e => setFilters(p => ({ ...p, timeTo: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>

            {/* Vendor filter — hidden for zone_coverage (not vendor-based) */}
            {selectedType !== "zone_coverage" && (
              <div className="relative" ref={vendorDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("reports_vendor_filter")}</label>
                <button type="button"
                  onClick={() => { setVendorDropdownOpen(o => !o); setVendorSearch(""); }}
                  className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 text-left">
                  <span className="truncate text-gray-700">
                    {filters.vendorId === "all"
                      ? t("reports_all_vendors")
                      : (() => { const v = vendors.find(x => x.id === filters.vendorId); return v ? `${v.first_name || ""} ${v.last_name || ""}`.trim() : t("reports_all_vendors"); })()
                    }
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                </button>
                {vendorDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg">
                    <div className="p-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                        <input autoFocus type="text" placeholder={t("reports_search_vendors")}
                          value={vendorSearch}
                          onChange={e => setVendorSearch(e.target.value)}
                          className="w-full pl-8 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                      </div>
                    </div>
                    <ul className="max-h-48 overflow-y-auto pb-1">
                      <li>
                        <button type="button"
                          onClick={() => { setFilters(p => ({ ...p, vendorId: "all" })); setVendorDropdownOpen(false); setVendorSearch(""); }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${filters.vendorId === "all" ? "bg-green-50 text-green-700 font-medium" : "text-gray-700"}`}>
                          {t("reports_all_vendors")}
                        </button>
                      </li>
                      {vendors
                        .filter(v => !vendorSearch || `${v.first_name || ""} ${v.last_name || ""}`.toLowerCase().includes(vendorSearch.toLowerCase()))
                        .map(v => (
                          <li key={v.id}>
                            <button type="button"
                              onClick={() => { setFilters(p => ({ ...p, vendorId: v.id })); setVendorDropdownOpen(false); setVendorSearch(""); }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${filters.vendorId === v.id ? "bg-green-50 text-green-700 font-medium" : "text-gray-700"}`}>
                              {v.first_name} {v.last_name}
                            </button>
                          </li>
                        ))}
                      {vendors.filter(v => !vendorSearch || `${v.first_name || ""} ${v.last_name || ""}`.toLowerCase().includes(vendorSearch.toLowerCase())).length === 0 && vendorSearch && (
                        <li className="px-3 py-2 text-sm text-gray-400 italic">{t("reports_no_vendors_found")}</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Zone filter — hidden for activity only */}
            {selectedType !== "activity" && (
              <div className="relative" ref={zoneDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("reports_zone_filter")}</label>
                <button type="button"
                  onClick={() => { setZoneDropdownOpen(o => !o); setZoneSearch(""); }}
                  className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 text-left">
                  <span className="truncate text-gray-700">
                    {filters.zoneId === "all"
                      ? t("reports_all_zones")
                      : (sections.find(s => s.id === filters.zoneId)?.name || t("reports_all_zones"))
                    }
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                </button>
                {zoneDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg">
                    <div className="p-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                        <input autoFocus type="text" placeholder={t("reports_search_zones")}
                          value={zoneSearch}
                          onChange={e => setZoneSearch(e.target.value)}
                          className="w-full pl-8 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                      </div>
                    </div>
                    <ul className="max-h-48 overflow-y-auto pb-1">
                      <li>
                        <button type="button"
                          onClick={() => { setFilters(p => ({ ...p, zoneId: "all" })); setZoneDropdownOpen(false); setZoneSearch(""); }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${filters.zoneId === "all" ? "bg-green-50 text-green-700 font-medium" : "text-gray-700"}`}>
                          {t("reports_all_zones")}
                        </button>
                      </li>
                      {sections
                        .filter(s => !zoneSearch || (s.name || "").toLowerCase().includes(zoneSearch.toLowerCase()))
                        .map(s => (
                          <li key={s.id}>
                            <button type="button"
                              onClick={() => { setFilters(p => ({ ...p, zoneId: s.id })); setZoneDropdownOpen(false); setZoneSearch(""); }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${filters.zoneId === s.id ? "bg-green-50 text-green-700 font-medium" : "text-gray-700"}`}>
                              {s.name}
                            </button>
                          </li>
                        ))}
                      {sections.filter(s => !zoneSearch || (s.name || "").toLowerCase().includes(zoneSearch.toLowerCase())).length === 0 && zoneSearch && (
                        <li className="px-3 py-2 text-sm text-gray-400 italic">{t("reports_no_zones_found")}</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Custom Report — event type selector */}
          {selectedType === "custom" && (
            <div className="mt-4 border border-gray-200 rounded-xl p-4 bg-gray-50">
              <p className="text-xs font-semibold text-gray-600 mb-3">{t("reports_event_types")}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {ALL_CUSTOM_TYPES.map(evt => (
                  <label key={evt.id} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input type="checkbox"
                      checked={customEventTypes.includes(evt.id)}
                      onChange={e => {
                        if (e.target.checked) setCustomEventTypes(prev => [...prev, evt.id]);
                        else setCustomEventTypes(prev => prev.filter(x => x !== evt.id));
                      }}
                      className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                    />
                    <span className="text-gray-700 text-xs">{evt.label}</span>
                  </label>
                ))}
              </div>
              {customEventTypes.length === 0 && (
                <p className="text-xs text-red-500 mt-2">{t("reports_select_event_type")}</p>
              )}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs font-semibold text-gray-600 mb-2">{t("reports_group_by")}</p>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { id: "vendor", labelKey: "reports_group_vendor" },
                    { id: "zone",   labelKey: "reports_group_zone"   },
                    { id: "day",    labelKey: "reports_group_day"    },
                    { id: "client", labelKey: "reports_group_client" },
                  ].map(g => (
                    <button key={g.id} type="button"
                      onClick={() => setCustomGroupBy(g.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                        customGroupBy === g.id
                          ? "bg-green-600 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}>
                      {t(g.labelKey)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <button onClick={generateReport} disabled={loading || (selectedType === "custom" && customEventTypes.length === 0)}
            className="mt-4 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-6 py-2 rounded-lg transition disabled:opacity-50">
            {loading ? t("reports_generating") : "📊 " + t("reports_generate")}
          </button>
        </div>
      )}

      {/* Error */}
      {error && selectedType !== "client_creation" && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
      )}

      {/* Results — not shown for client_creation (it has its own results section below) */}
      {generated && selectedType !== "client_creation" && (
        <div className="bg-white rounded-xl shadow-sm p-6">

          {/* Results header */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">
                {REPORT_TYPES.find(rt => rt.id === selectedType)?.label} {t("reports_report_label")}
              </h3>
              <p className="text-gray-400 text-xs mt-0.5">
                {selectedType === "zone_coverage"
                  ? `${zoneCoverage.length} ${t("reports_col_zone")}(s)`
                  : selectedType === "unauthorized_zone"
                  ? `${Object.keys(unauthorizedZoneActivity).length} ${t("reports_col_vendor")}(s) · ${results.length} event(s)`
                  : selectedType === "zone"
                  ? `${Object.keys(zoneActivity).length} ${t("reports_col_zone")}(s) · ${results.length} event(s)`
                  : `${results.length} event(s)`
                } · {filters.dateFrom} {filters.timeFrom} → {filters.dateTo} {filters.timeTo}
              </p>
            </div>

            {/* Download buttons — show if there's data */}
            {(results.length > 0 || zoneCoverage.length > 0) && (
              <div className="flex gap-2">
                <button onClick={exportExcel}
                  className="text-xs px-3 py-1.5 bg-green-50 text-green-700 hover:bg-green-100 rounded-lg font-medium transition">
                  📊 {t("reports_export_excel")}
                </button>
                <button onClick={exportCSV}
                  className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg font-medium transition">
                  📄 {t("reports_export_csv")}
                </button>
                <button onClick={exportPDF}
                  className="text-xs px-3 py-1.5 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg font-medium transition">
                  📑 {t("reports_export_pdf")}
                </button>
              </div>
            )}
          </div>

          {/* ── EMPTY STATES ── */}
          {showEmpty && (
            <div className="text-center py-12">
              <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">{t("reports_no_events")}</p>
              <p className="text-gray-400 text-sm mt-1">{t("reports_no_events_sub")}</p>
            </div>
          )}

          {showZoneEmpty && (
            <div className="text-center py-12">
              <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">{t("reports_no_clients_zone")}</p>
              <p className="text-gray-400 text-sm mt-1">{t("reports_no_clients_zone_sub")}</p>
            </div>
          )}

          {showZoneActivityEmpty && (
            <div className="text-center py-12">
              <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">{t("reports_no_zone_visits")}</p>
              <p className="text-gray-400 text-sm mt-1">{t("reports_no_zone_visits_sub")}</p>
            </div>
          )}

          {showUnauthorizedZoneEmpty && (
            <div className="text-center py-12">
              <XCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">{t("reports_no_unauth")}</p>
              <p className="text-gray-400 text-sm mt-1">{t("reports_no_unauth_sub")}</p>
            </div>
          )}

          {/* Anomaly detection disabled notice — for Out-of-Zone and Unauthorized Zone */}
          {generated && (selectedType === "alerts" || selectedType === "unauthorized_zone") && !anomalyEnabled && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
              <p className="text-yellow-700 text-sm">
                ⚠️ {t("reports_anomaly_disabled")}
              </p>
            </div>
          )}

          {/* ── ZONE COVERAGE VIEW ── */}
          {generated && selectedType === "zone_coverage" && zoneCoverage.length > 0 && (
            <div className="space-y-4">
              {/* Summary bar across all zones */}
              {(() => {
                const totalAll   = zoneCoverage.reduce((s, z) => s + z.totalClients, 0);
                const visitedAll = zoneCoverage.reduce((s, z) => s + z.visitedClients, 0);
                const pctAll     = totalAll > 0 ? Math.round((visitedAll / totalAll) * 100) : 0;
                return (
                  <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-6">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">{t("reports_overall_coverage")}</p>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div className="h-3 rounded-full bg-green-500 transition-all"
                          style={{ width: `${pctAll}%` }} />
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-2xl font-bold text-green-600">{pctAll}%</p>
                      <p className="text-xs text-gray-400">{visitedAll} / {totalAll} {t("reports_clients_count")}</p>
                    </div>
                  </div>
                );
              })()}

              {/* Per-zone cards */}
              {zoneCoverage.map((zone, i) => {
                const pct = zone.totalClients > 0
                  ? Math.round((zone.visitedClients / zone.totalClients) * 100)
                  : 0;
                return (
                  <div key={i} className="border border-gray-200 rounded-xl p-4">
                    {/* Zone header */}
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">📍</span>
                        <span className="font-semibold text-gray-800">{zone.sectionName}</span>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                          {zone.totalClients} {t("reports_clients_count")}
                        </span>
                      </div>
                      <span className={`text-sm font-bold px-3 py-1 rounded-full ${
                        pct === 100 ? "bg-green-100 text-green-700" :
                        pct >= 50   ? "bg-yellow-100 text-yellow-700" :
                                      "bg-red-100 text-red-700"
                      }`}>
                        {pct}% {t("reports_covered")}
                      </span>
                    </div>

                    {/* Progress bar */}
                    <div className="w-full bg-gray-100 rounded-full h-2.5 mb-3">
                      <div className="h-2.5 rounded-full bg-green-500 transition-all"
                        style={{ width: `${pct}%` }} />
                    </div>

                    {/* Visited / Not Visited columns */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-green-50 rounded-lg p-3">
                        <p className="text-xs font-semibold text-green-700 mb-2">
                          ✅ {t("reports_visited")} ({zone.visitedClients})
                        </p>
                        {zone.visited.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">{t("reports_none_visited")}</p>
                        ) : (
                          <ul className="text-xs text-green-800 space-y-0.5 max-h-32 overflow-y-auto">
                            {zone.visited.map((name, j) => (
                              <li key={j} className="flex items-center gap-1">
                                <span className="text-green-500">•</span> {name}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="bg-red-50 rounded-lg p-3">
                        <p className="text-xs font-semibold text-red-700 mb-2">
                          ❌ {t("reports_not_visited")} ({zone.totalClients - zone.visitedClients})
                        </p>
                        {zone.notVisited.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">{t("reports_all_visited")} 🎉</p>
                        ) : (
                          <ul className="text-xs text-red-800 space-y-0.5 max-h-32 overflow-y-auto">
                            {zone.notVisited.map((name, j) => (
                              <li key={j} className="flex items-center gap-1">
                                <span className="text-red-400">•</span> {name}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── ZONE ACTIVITY VIEW (Zone/Geofence report) ── */}
          {generated && selectedType === "zone" && Object.keys(zoneActivity).length > 0 && (
            <div className="space-y-4">
              {Object.entries(zoneActivity)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([zoneName, zEvents]) => (
                  <div key={zoneName} className="border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className="w-4 h-4 text-purple-500" />
                      <span className="font-semibold text-gray-800">{zoneName}</span>
                      <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full">
                        {zEvents.length} visit{zEvents.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {zEvents.map((e, i) => (
                        <div key={i} className="flex items-center gap-3 text-xs py-1.5">
                          <span className="font-medium text-gray-700 w-28 truncate">{e.vendor_name || "—"}</span>
                          <span className="text-gray-500 flex-1 truncate">{e.client_name || "—"}</span>
                          <span className="text-gray-400 shrink-0">{formatTimestamp(e.timestamp)}</span>
                          <span className={`shrink-0 px-1.5 py-0.5 rounded-full font-medium ${e.auto ? "bg-blue-50 text-blue-600" : "bg-orange-50 text-orange-600"}`}>
                            {e.auto ? "GPS" : "Manual"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* ── UNAUTHORIZED ZONE VIEW ── */}
          {generated && selectedType === "unauthorized_zone" && Object.keys(unauthorizedZoneActivity).length > 0 && (
            <div className="space-y-4">
              {Object.entries(unauthorizedZoneActivity)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([vendorName, vEvents]) => (
                  <div key={vendorName} className="border border-red-100 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span className="font-semibold text-gray-800">{vendorName}</span>
                      <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full">
                        {vEvents.length} violation{vEvents.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {vEvents.map((e, i) => {
                        const secName = (e.section && sections.find(s => s.id === e.section)?.name) || e.section_name || e.section || "Unknown Zone";
                        return (
                          <div key={i} className="flex items-center gap-3 text-xs py-1.5">
                            <span className="text-red-600 flex-1 truncate font-medium">{secName}</span>
                            <span className="text-gray-500 truncate">{e.client_name || "—"}</span>
                            <span className="text-gray-400 shrink-0">{formatTimestamp(e.timestamp)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* ── VISIT SUMMARY TABLE ── */}
          {generated && selectedType === "visits" && visitSummary.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500">{t("reports_col_vendor")}</th>
                    <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500">{t("reports_col_total_visits")}</th>
                    <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500">{t("reports_col_unique_clients")}</th>
                    <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500">{t("reports_col_last_visit")}</th>
                  </tr>
                </thead>
                <tbody>
                  {visitSummary.map((row, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-xs">
                            {row.vendorName.charAt(0)}
                          </div>
                          <span className="text-gray-800 text-sm font-medium">{row.vendorName}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full">
                          {row.totalVisits}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600 text-sm">{row.clients.size}</td>
                      <td className="py-3 px-4 text-gray-400 text-xs">
                        {row.lastVisit ? row.lastVisit.toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── DAILY SUMMARY VIEW ── */}
          {generated && selectedType === "daily" && results.length > 0 && (() => {
            const checkins    = results.filter(e => e.type === "checkin" || e.type === "visit");
            const speedAlerts = results.filter(e => e.type === "speed_alert");
            const zoneAlerts  = results.filter(e => e.type === "zone_alert");
            const longStops   = results.filter(e => e.type === "long_stop_alert");
            const byVendor    = {};
            checkins.forEach(e => {
              const name = e.vendor_name || e.vendor_id || "Unknown";
              if (!byVendor[name]) byVendor[name] = { checkins: 0, clients: new Set() };
              byVendor[name].checkins++;
              if (e.client_id || e.clientId) byVendor[name].clients.add(e.client_id || e.clientId);
            });
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { labelKey: "reports_check_ins",         value: checkins.length,    bg: "bg-green-50",  text: "text-green-700"  },
                    { labelKey: "reports_speed_alerts",      value: speedAlerts.length, bg: "bg-red-50",    text: "text-red-700"    },
                    { labelKey: "reports_zone_alerts",       value: zoneAlerts.length,  bg: "bg-orange-50", text: "text-orange-700" },
                    { labelKey: "reports_long_stop_alerts",  value: longStops.length,   bg: "bg-yellow-50", text: "text-yellow-700" },
                  ].map(s => (
                    <div key={s.labelKey} className={`${s.bg} rounded-xl p-4 text-center`}>
                      <p className={`text-2xl font-bold ${s.text}`}>{s.value}</p>
                      <p className="text-xs text-gray-500 mt-1">{t(s.labelKey)}</p>
                    </div>
                  ))}
                </div>
                {Object.keys(byVendor).length > 0 && (
                  <div className="overflow-x-auto rounded-xl border border-gray-100">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="py-2 px-4 text-left text-xs font-semibold text-gray-500">{t("reports_col_vendor")}</th>
                          <th className="py-2 px-4 text-center text-xs font-semibold text-gray-500">{t("reports_check_ins")}</th>
                          <th className="py-2 px-4 text-center text-xs font-semibold text-gray-500">{t("reports_col_clients_visited")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {Object.entries(byVendor).map(([name, d]) => (
                          <tr key={name} className="hover:bg-gray-50">
                            <td className="py-2 px-4 font-medium text-gray-800 text-sm">{name}</td>
                            <td className="py-2 px-4 text-center">
                              <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">{d.checkins}</span>
                            </td>
                            <td className="py-2 px-4 text-center text-gray-600 text-sm">{d.clients.size}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── CUSTOM REPORT GROUPED VIEW ── */}
          {generated && selectedType === "custom" && results.length > 0 && (() => {
            const grouped = {};
            results.forEach(e => {
              let key;
              if (customGroupBy === "vendor") {
                key = e.vendor_name || e.vendor_id || "Unknown Vendor";
              } else if (customGroupBy === "zone") {
                const secName = (e.section && sections.find(s => s.id === e.section)?.name) || e.section || "No Zone";
                key = secName;
              } else if (customGroupBy === "day") {
                const d = e.timestamp?.toDate ? e.timestamp.toDate() : new Date(e.timestamp || 0);
                key = d.toISOString().slice(0, 10);
              } else {
                key = e.client_name || e.client_id || "No Client";
              }
              if (!grouped[key]) grouped[key] = [];
              grouped[key].push(e);
            });
            const groupKeys = Object.keys(grouped).sort();
            return (
              <div className="space-y-3">
                {groupKeys.map(groupKey => (
                  <div key={groupKey} className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b border-gray-100">
                      <span className="font-semibold text-gray-800 text-sm">{groupKey}</span>
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                        {grouped[groupKey].length} event{grouped[groupKey].length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {grouped[groupKey].map((e, i) => (
                        <div key={i} className="flex items-center gap-3 px-4 py-2 text-xs hover:bg-gray-50">
                          <span className={`px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${eventColors[e.type] || "bg-gray-100 text-gray-700"}`}>
                            {e.type}
                          </span>
                          {customGroupBy !== "vendor" && (
                            <span className="text-gray-700 truncate">{e.vendor_name || e.vendor_id || "—"}</span>
                          )}
                          {customGroupBy !== "client" && (
                            <span className="text-gray-500 flex-1 truncate">{e.client_name || "—"}</span>
                          )}
                          <span className="text-gray-400 flex-shrink-0">{formatTimestamp(e.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* ── GENERIC EVENTS TABLE ── */}
          {generated && !["visits", "zone_coverage", "zone", "unauthorized_zone", "daily", "custom"].includes(selectedType) && results.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">{t("reports_col_type")}</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">{t("reports_col_vendor")}</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">{t("reports_col_client_zone")}</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">{t("reports_col_timestamp")}</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">{t("reports_col_duration")}</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map(event => (
                    <tr key={event.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${eventColors[event.type] || "bg-gray-100 text-gray-700"}`}>
                          {event.type}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-gray-700 text-xs">{event.vendor_name || event.vendor_id || "—"}</td>
                      <td className="py-2 px-3 text-gray-700 text-xs">{event.client_name || event.section_name || "—"}</td>
                      <td className="py-2 px-3 text-gray-500 text-xs">{formatTimestamp(event.timestamp)}</td>
                      <td className="py-2 px-3 text-gray-500 text-xs">{event.duration ? `${event.duration}s` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>
      )}

      {/* ── Client Creation Report ─────────────────────────────────────────── */}
      {selectedType === "client_creation" && (
        <div className="space-y-4">

          {/* Date range + generate */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("reports_step2")}</h3>
            {/* Quick period buttons */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {[
                { key: "today",      labelKey: "reports_period_today"      },
                { key: "yesterday",  labelKey: "reports_period_yesterday"  },
                { key: "this_week",  labelKey: "reports_period_this_week"  },
                { key: "this_month", labelKey: "reports_period_this_month" },
                { key: "custom",     labelKey: "reports_period_custom"     },
              ].map(p => (
                <button key={p.key} onClick={() => setOpPeriod(p.key)}
                  className={`text-xs px-3 py-1.5 rounded-lg font-medium transition ${
                    opQuickFilter === p.key
                      ? "bg-green-600 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}>
                  {t(p.labelKey)}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("reports_from_date")}</label>
                <input type="date" value={opDateFrom}
                  onChange={e => { setOpDateFrom(e.target.value); setOpQuickFilter("custom"); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("reports_to_date")}</label>
                <input type="date" value={opDateTo}
                  onChange={e => { setOpDateTo(e.target.value); setOpQuickFilter("custom"); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("reports_op_operator")}</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder={t("reports_op_search")}
                      value={opOperatorSearch}
                      onChange={e => setOpOperatorSearch(e.target.value)}
                      className="w-full pl-8 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <select value={opOperatorFilter}
                    onChange={e => setOpOperatorFilter(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white min-w-[200px]">
                    <option value="all">{t("reports_op_all_operators")}</option>
                    {opOperators
                      .filter(op => {
                        if (!opOperatorSearch) return true;
                        return `${op.first_name || ""} ${op.last_name || ""} ${op.email || ""}`
                          .toLowerCase().includes(opOperatorSearch.toLowerCase());
                      })
                      .map(op => (
                        <option key={op.id} value={op.id}>
                          {op.first_name} {op.last_name} — {op.email}
                        </option>
                      ))}
                  </select>
                </div>
                {opOperators.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">{t("reports_op_loading")}</p>
                )}
              </div>
            </div>
            <button onClick={generateClientCreationReport} disabled={opLoading}
              className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white font-medium py-2.5 rounded-lg flex items-center justify-center gap-2 transition">
              {opLoading
                ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />{t("reports_generating")}</>
                : <><HardHat className="w-4 h-4" />{t("reports_generate")}</>
              }
            </button>
          </div>

          {/* Results */}
          {opGenerated && (
            <div className="bg-white rounded-xl shadow-sm p-6 space-y-5">

              {/* Header + export */}
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700">{t("reports_op_report_title")}</h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {opDateFrom} → {opDateTo} · {opClients.length} client{opClients.length !== 1 ? "s" : ""} total
                  </p>
                </div>
                <button onClick={exportClientCreationExcel}
                  className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition">
                  <Download className="w-4 h-4" />
                  {t("reports_op_export")}
                </button>
              </div>

              {/* Summary table — by operator */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t("reports_op_by_operator")}</h4>
                <div className="overflow-x-auto rounded-lg border border-gray-100">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="py-2 px-3 text-left text-xs font-semibold text-gray-500">{t("reports_op_operator")}</th>
                        <th className="py-2 px-3 text-center text-xs font-semibold text-gray-500">{t("reports_op_today")}</th>
                        <th className="py-2 px-3 text-center text-xs font-semibold text-gray-500">{t("reports_op_this_week")}</th>
                        <th className="py-2 px-3 text-center text-xs font-semibold text-gray-500">{t("reports_op_this_month")}</th>
                        <th className="py-2 px-3 text-center text-xs font-semibold text-gray-500">{t("reports_op_in_period")}</th>
                        <th className="py-2 px-3 text-center text-xs font-semibold text-gray-500">{t("reports_op_total_all_time")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {opOperators.length === 0 ? (
                        <tr><td colSpan={6} className="py-6 text-center text-gray-400 text-sm">{t("reports_op_no_operators")}</td></tr>
                      ) : opOperators.map(op => {
                        const now      = new Date();
                        const todayStr = now.toISOString().slice(0, 10);
                        const weekAgo  = new Date(now); weekAgo.setDate(now.getDate() - 7);
                        const monthAgo = new Date(now); monthAgo.setMonth(now.getMonth() - 1);
                        const getD     = ts => ts?.toDate ? ts.toDate() : new Date(ts);
                        const inPeriod  = opClients.filter(c => c.created_by === op.id).length;
                        const allForOp  = Object.keys(opAllTimeCounts).length > 0
                          ? (opAllTimeCounts[op.id] ?? 0)
                          : inPeriod;
                        // today/week/month use in-period clients that fall in those windows
                        const todayN   = opClients.filter(c => c.created_by === op.id && c.created_at && getD(c.created_at).toISOString().slice(0,10) === todayStr).length;
                        const weekN    = opClients.filter(c => c.created_by === op.id && c.created_at && getD(c.created_at) >= weekAgo).length;
                        const monthN   = opClients.filter(c => c.created_by === op.id && c.created_at && getD(c.created_at) >= monthAgo).length;
                        return (
                          <tr key={op.id} className="hover:bg-gray-50">
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-bold shrink-0">
                                  {(op.first_name?.[0] || "?").toUpperCase()}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-700">{op.first_name} {op.last_name}</p>
                                  <p className="text-xs text-gray-400">{op.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-2 px-3 text-center text-sm font-semibold text-gray-700">{todayN}</td>
                            <td className="py-2 px-3 text-center text-sm font-semibold text-gray-700">{weekN}</td>
                            <td className="py-2 px-3 text-center text-sm font-semibold text-gray-700">{monthN}</td>
                            <td className="py-2 px-3 text-center">
                              <span className="text-sm font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">{inPeriod}</span>
                            </td>
                            <td className="py-2 px-3 text-center text-sm font-semibold text-gray-700">{allForOp}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Client detail list — searchable */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  {t("reports_op_all_clients")} ({opClients.length})
                </h4>

                {/* Search */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="text" value={opSearch}
                    onChange={e => setOpSearch(e.target.value)}
                    placeholder={t("reports_op_search_clients")}
                    className="w-full pl-9 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>

                {opClients.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 text-sm">{t("reports_op_no_clients")}</div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-100">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="py-2 px-3 text-left text-xs font-semibold text-gray-500">{t("reports_op_col_name")}</th>
                          <th className="py-2 px-3 text-left text-xs font-semibold text-gray-500">{t("reports_op_col_phone")}</th>
                          <th className="py-2 px-3 text-left text-xs font-semibold text-gray-500">{t("reports_op_col_created_by")}</th>
                          <th className="py-2 px-3 text-left text-xs font-semibold text-gray-500">{t("reports_op_col_date_added")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {opClients
                          .filter(c => {
                            const op  = opOperators.find(o => o.id === c.created_by);
                            const opName = op ? `${op.first_name || ""} ${op.last_name || ""}` : (c.created_by_name || "");
                            return `${c.first_name} ${c.last_name} ${c.phone} ${opName}`
                              .toLowerCase().includes(opSearch.toLowerCase());
                          })
                          .map(c => {
                            const op = opOperators.find(o => o.id === c.created_by);
                            const opName = op
                              ? `${op.first_name || ""} ${op.last_name || ""}`.trim()
                              : (c.created_by_name || "—");
                            return (
                              <tr key={c.id} className="hover:bg-gray-50">
                                <td className="py-2 px-3 font-medium text-gray-700 text-xs">
                                  {c.first_name} {c.last_name}
                                </td>
                                <td className="py-2 px-3 text-gray-500 text-xs">{c.phone || "—"}</td>
                                <td className="py-2 px-3 text-xs">
                                  <span className="bg-orange-50 text-orange-600 border border-orange-100 px-2 py-0.5 rounded-full">
                                    {opName}
                                  </span>
                                </td>
                                <td className="py-2 px-3 text-gray-400 text-xs">
                                  {c.created_at ? formatTimestamp(c.created_at) : "—"}
                                </td>
                              </tr>
                            );
                          })
                        }
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}

export default ReportsPage;